/**
 * useAppTheme Hook
 *
 * Determines the appropriate theme based on:
 * 1. User preference (light/dark/system)
 * 2. Admin vs Tenant context (auto-detected from isInternalAdmin)
 *
 * Returns one of:
 * - TenantLightTheme (default)
 * - TenantDarkTheme
 * - AdminLightTheme
 * - AdminDarkTheme
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  TenantLightTheme,
  TenantDarkTheme,
  AdminLightTheme,
  AdminDarkTheme,
} from '../theme/index';

/**
 * Custom hook for theme selection
 * @returns {Object} MUI theme object
 */
export const useAppTheme = () => {
  const auth = useSelector((state) => state.auth);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  const theme = useMemo(() => {
    // If user is not authenticated, always use TenantLightTheme (login screen)
    if (!auth?.isAuthenticated) {
      return TenantLightTheme;
    }

    // User preference: 'light', 'dark', or 'system' (default)
    const userPref = auth?.user?.theme || 'system';

    // Determine if dark mode should be used
    let isDark;
    if (userPref === 'system') {
      isDark = prefersDark;
    } else {
      isDark = userPref === 'dark';
    }

    // Determine if user is admin (internal admin without tenant context)
    const isAdmin = auth?.isInternalAdmin === true;

    // Select appropriate theme
    if (isAdmin) {
      return isDark ? AdminDarkTheme : AdminLightTheme;
    }
    return isDark ? TenantDarkTheme : TenantLightTheme;
  }, [auth?.isAuthenticated, auth?.user?.theme, auth?.isInternalAdmin, prefersDark]);

  return theme;
};

/**
 * Get theme info for display purposes
 * @returns {Object} Theme information
 */
export const useThemeInfo = () => {
  const auth = useSelector((state) => state.auth);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  return useMemo(() => {
    const userPref = auth?.user?.theme || 'system';
    const isAdmin = auth?.isInternalAdmin === true;

    let isDark;
    if (userPref === 'system') {
      isDark = prefersDark;
    } else {
      isDark = userPref === 'dark';
    }

    return {
      preference: userPref,
      systemPrefersDark: prefersDark,
      isAdmin,
      isDark,
      themeName: isAdmin
        ? isDark ? 'AdminDark' : 'AdminLight'
        : isDark ? 'TenantDark' : 'TenantLight',
    };
  }, [auth?.user?.theme, auth?.isInternalAdmin, prefersDark]);
};

export default useAppTheme;
